import React, { useEffect, useState, useCallback, memo } from "react";
import {
  Container,
  Typography,
  Button,
  Box,
} from "@mui/material";
import { auth, useAppContext } from "../../context/AppContext";
import {
  FileLibrary,
  buildFileLibrary
} from "../../context/FileLibraryContext";
import { sendAxiosPost } from "../../util/ServerUtil";
import SeriesEditor from "./SeriesEditor";
import { Link } from "react-router-dom";

const VideoDataManager: React.FC = () => {
  const { isAdmin } = useAppContext();
  const [library, setLibrary] = useState<FileLibrary>({ series: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Video Manager | ipwd.club";
    (async () => {
      try {
        const resp = await sendAxiosPost("/api/admin/getRawVideoData", null, true);
        const lib = buildFileLibrary(resp.data);
        setLibrary(lib ?? { series: [] });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateLibrary = useCallback((newLib: FileLibrary) => {
    setLibrary(newLib);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const json = JSON.stringify(library.series);
      // save json to local file (download)
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "video_data.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Failed to save video data");
    } finally {
      setSaving(false);
    }
  }, [library]);

  if (!isAdmin) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h4">Access Denied</Typography>
        <Typography>You do not have permission to access this page.</Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h4">Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Container sx={{ mb: 2 }}>
          <Link to="/admin/serverLogs" className="btn">Audit Logs</Link>
          <Link to="/admin/userManager" className="btn">User Manager</Link>
          <Link to="/admin/videoDataManager" className="btn-active">Video Data Manager</Link>
      </Container>
      <Typography variant="h4" gutterBottom>
        Video Data Manager
      </Typography>
      <Box sx={{ mb: 4 }}>
        <SeriesEditor
          seriesList={library.series}
          onChange={updateLibrary}
        />
      </Box>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        disabled={saving}
        sx={{ mt: 4 }}
      >
        {saving ? "Saving..." : "Save Changes"}
      </Button>
    </Container>
  );
};

export default memo(VideoDataManager);
